import { Layout } from '../components/Layout';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Factory, Package, ArrowRightLeft, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function Dashboard({ user, onLogout }) {
  const [dailyData, setDailyData] = useState(null);
  const [inventorySummary, setInventorySummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [dailyRes, inventoryRes] = await Promise.all([
        axios.get(`${API}/dashboard/daily`, getAuthHeader()),
        axios.get(`${API}/dashboard/inventory-summary`, getAuthHeader()),
      ]);
      setDailyData(dailyRes.data);
      setInventorySummary(inventoryRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-full">
          <div className="text-stone-400">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-bold text-stone-900">Dashboard</h1>
          <p className="text-stone-600 mt-1">Daily operations overview</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 border-l-4 border-secondary shadow-sm" data-testid="production-metric">
            <div className="flex items-center justify-between mb-4">
              <Factory className="text-secondary" size={24} />
              <span className="text-xs font-medium text-stone-500 uppercase tracking-wider">Production</span>
            </div>
            <div className="font-mono text-3xl font-bold text-stone-900">{dailyData?.production?.total_batches || 0}</div>
            <div className="text-sm text-stone-600 mt-1">Batches Today</div>
          </div>

          <div className="bg-white p-6 border-l-4 border-functional-info shadow-sm" data-testid="inventory-metric">
            <div className="flex items-center justify-between mb-4">
              <Package className="text-functional-info" size={24} />
              <span className="text-xs font-medium text-stone-500 uppercase tracking-wider">Inventory</span>
            </div>
            <div className="font-mono text-3xl font-bold text-stone-900">{inventorySummary?.stock_items || 0}</div>
            <div className="text-sm text-stone-600 mt-1">Stock Items</div>
          </div>

          <div className="bg-white p-6 border-l-4 border-accent shadow-sm" data-testid="transfers-metric">
            <div className="flex items-center justify-between mb-4">
              <ArrowRightLeft className="text-accent" size={24} />
              <span className="text-xs font-medium text-stone-500 uppercase tracking-wider">Transfers</span>
            </div>
            <div className="font-mono text-3xl font-bold text-stone-900">{dailyData?.transfers?.total || 0}</div>
            <div className="text-sm text-stone-600 mt-1">Requests Today</div>
          </div>

          <div className="bg-white p-6 border-l-4 border-functional-error shadow-sm" data-testid="wastage-metric">
            <div className="flex items-center justify-between mb-4">
              <Trash2 className="text-functional-error" size={24} />
              <span className="text-xs font-medium text-stone-500 uppercase tracking-wider">Wastage</span>
            </div>
            <div className="font-mono text-3xl font-bold text-stone-900">₹{dailyData?.wastage?.total_cost?.toFixed(0) || 0}</div>
            <div className="text-sm text-stone-600 mt-1">Cost Today</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-6">
            <h3 className="font-heading text-xl font-semibold text-stone-800 mb-4">Today's Production</h3>
            {dailyData?.production?.batches?.length > 0 ? (
              <div className="space-y-3">
                {dailyData.production.batches.slice(0, 5).map((batch) => (
                  <div key={batch.batch_id} className="flex justify-between items-center py-2 border-b border-stone-100">
                    <div>
                      <div className="font-medium text-stone-900">{batch.type}</div>
                      <div className="text-sm text-stone-500 font-mono">{batch.batch_id}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-semibold text-stone-900">{batch.net_usable} {batch.unit}</div>
                      <div className="text-xs text-stone-500">Net Usable</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-stone-400">No production batches today</div>
            )}
          </div>

          <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-6">
            <h3 className="font-heading text-xl font-semibold text-stone-800 mb-4">Transfer Status</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-4 bg-stone-50 rounded-sm">
                <span className="text-stone-700">Pending Approval</span>
                <span className="font-mono font-bold text-functional-warning">{dailyData?.transfers?.pending || 0}</span>
              </div>
              <div className="flex justify-between items-center p-4 bg-stone-50 rounded-sm">
                <span className="text-stone-700">Dispatched</span>
                <span className="font-mono font-bold text-functional-info">{dailyData?.transfers?.dispatched || 0}</span>
              </div>
              <div className="flex justify-between items-center p-4 bg-stone-50 rounded-sm">
                <span className="text-stone-700">Received</span>
                <span className="font-mono font-bold text-functional-success">{dailyData?.transfers?.received || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
